<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { QuickActionSearchEmits, QuickActionSearchProps } from "@/dsComponents/QuickActions/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

const { t } = useI18n();

const props = withDefaults(defineProps<QuickActionSearchProps>(), {
    icon: "search",
    disabled: false,
});

const emit = defineEmits<QuickActionSearchEmits>();

const isSearchInputFocused = ref(props.isFocused);

const searchHeaderClasses = computed(() => {
    return [
        "search",
        {
            "search--focused": isSearchInputFocused.value,
            "search--filled": props.modelValue,
            "search--disabled": props.disabled,
        },
    ];
});
</script>

<template>
    <div :class="searchHeaderClasses">
        <i :class="['search__icon', `ti ti-${props.icon}`]" />
        <TextInputAlt
            :modelValue="props.modelValue"
            class="search__input"
            size="md"
            variant="transparent"
            :placeholder="placeholder || t('global.placeholder.search')"
            :debounce="300"
            :disabled="disabled"
            :focused="isFocused"
            data-bot="search"
            @update:modelValue="emit('update:modelValue', $event)"
            @keydown="emit('keydown', $event)"
            @focus="
                () => {
                    isSearchInputFocused = true;
                }
            "
            @blur="
                () => {
                    isSearchInputFocused = false;
                }
            "
        />
        <ButtonAlt
            v-if="!!modelValue && !disabled"
            class="search__clear"
            type="neutral"
            size="sm"
            iconOnly="x"
            data-bot="clear-search"
            @click="emit('clear')"
        />
    </div>
</template>

<style scoped lang="scss">
.search {
    display: flex;
    align-items: center;
    gap: var(--spacing-lg, 12px);
    padding: 0 var(--spacing-lg, 12px);
    width: 100%;
    height: 48px;

    &:hover:not(&--disabled),
    &--focused:not(&--disabled),
    &--filled:not(&--disabled) {
        .search__icon {
            color: var(--text-main-900, #00061a);
        }
    }

    &--disabled {
        pointer-events: none;
        opacity: 0.55;
    }

    &__icon {
        font-size: 16px;
        color: var(--icon-sub-500, #636778);
    }
}
</style>
